import { useNavigate } from "react-router-dom";
import { AlertTriangle, Award, BookOpen, Play } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

type CyFunFunction = {
  id: string;
  name: string;
  score: number;
  maxScore: number;
};

const CYFUN_FUNCTIONS: CyFunFunction[] = [
  { id: "GV", name: "Guvernare", score: 3.2, maxScore: 5 },
  { id: "ID", name: "Identificare", score: 2.8, maxScore: 5 },
  { id: "PR", name: "Protecție", score: 3.5, maxScore: 5 },
  { id: "DE", name: "Detectare", score: 2.5, maxScore: 5 },
  { id: "RS", name: "Răspuns", score: 2.0, maxScore: 5 },
  { id: "RC", name: "Recuperare", score: 1.8, maxScore: 5 },
];

const MATURITY_SCALE = [
  { scor: 1, doc: "Nu există documentație", impl: "Nu este implementat" },
  {
    scor: 2,
    doc: "Documentație parțială, neactualizată",
    impl: "Implementare ad-hoc, inconsistentă",
  },
  {
    scor: 3,
    doc: "Documentație completă, dar nevalidată",
    impl: "Implementare parțială, în curs",
  },
  {
    scor: 4,
    doc: "Documentație aprobată și comunicată",
    impl: "Implementare completă, monitorizată",
  },
  {
    scor: 5,
    doc: "Documentație optimizată, revizuită periodic",
    impl: "Implementare matură, îmbunătățire continuă",
  },
];

type RadarChartProps = {
  data: CyFunFunction[];
};

const RadarChart = ({ data }: RadarChartProps) => {
  const size = 280;
  const center = size / 2;
  const maxRadius = 110;
  const levels = 5;

  const angleStep = (2 * Math.PI) / data.length;

  const getPoint = (index: number, value: number) => {
    const angle = index * angleStep - Math.PI / 2;
    const radius = (value / 5) * maxRadius;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    };
  };

  const polygonPoints = data
    .map((item, index) => {
      const point = getPoint(index, item.score);
      return `${point.x},${point.y}`;
    })
    .join(" ");

  return (
    <svg width={size} height={size} className="mx-auto">
      {[1, 2, 3, 4, 5].map((level) => (
        <circle
          key={level}
          cx={center}
          cy={center}
          r={(level / levels) * maxRadius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="1"
        />
      ))}

      {data.map((item, index) => {
        const point = getPoint(index, 5);
        return (
          <line
            key={item.id}
            x1={center}
            y1={center}
            x2={point.x}
            y2={point.y}
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        );
      })}

      <polygon
        points={polygonPoints}
        fill="rgba(59, 130, 246, 0.3)"
        stroke="#3b82f6"
        strokeWidth="2"
      />

      {data.map((item, index) => {
        const point = getPoint(index, item.score);
        return (
          <circle
            key={item.id}
            cx={point.x}
            cy={point.y}
            r="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="2"
          />
        );
      })}

      {data.map((item, index) => {
        const labelPoint = getPoint(index, 6.2);
        return (
          <text
            key={item.id}
            x={labelPoint.x}
            y={labelPoint.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-xs font-medium fill-slate-700"
          >
            {item.id}
          </text>
        );
      })}
    </svg>
  );
};

const GhidMaturitate = () => {
  const navigate = useNavigate();

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="md:mb-8 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="md:w-12 w-10 md:h-12 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <BookOpen size={24} className="text-blue-600" />
            </div>
            <div>
              <h1 className="font-heading md:text-2xl text-xl font-bold text-slate-800">
                Ghid Evaluarea Maturității
              </h1>
              <p className="md:text-sm text-xs text-slate-600">
                Cadrul CyFun® 2025
              </p>
            </div>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="md:text-lg text-base">
              Ce este CyFun® 2025?
            </CardTitle>
            <CardDescription className="md:text-sm text-xs">
              Cadrul oficial DNSC pentru evaluarea capacităților de securitate
            </CardDescription>
          </CardHeader>
          <CardContent className="md:text-sm text-xs pt-0 md:pt-0">
            <p className="text-slate-600 mb-4">
              Cadrul oficial DNSC care evaluează capacitatea organizației de a
              rezista atacurilor. Este structurat pe 6 funcții-pilon (Pilonii
              CyFun).
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-xl md:p-6 p-4">
                <h3 className="font-heading font-medium md:text-sm text-xs text-slate-800 text-center mb-4">
                  Diagrama Radar - Cele 6 Funcții
                </h3>
                <RadarChart data={CYFUN_FUNCTIONS} />
              </div>

              <div className="space-y-3">
                {CYFUN_FUNCTIONS.map((func) => (
                  <div
                    key={func.id}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl"
                  >
                    <div className="md:w-12 w-10 md:h-12 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="font-bold md:text-sm text-xs text-blue-700">
                        {func.id}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium md:text-sm text-xs text-slate-800">
                        {func.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{
                              width: `${(func.score / func.maxScore) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="md:text-sm text-xs font-medium text-slate-600">
                          {func.score}/{func.maxScore}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white mb-6">
          <h2 className="font-heading font-bold md:text-xl text-base mb-4 flex items-center gap-2">
            <Award size={24} />
            Evaluarea Dublă (Regulă de aur)
          </h2>
          <p className="text-blue-100 md:text-sm text-xs mb-4">
            Pentru fiecare cerință (ex: utilizarea MFA), trebuie să dai două
            note:
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-xl md:p-4 p-3">
              <h3 className="font-heading font-semibold md:text-sm text-xs mb-2">
                Maturitatea Documentației (1-5)
              </h3>
              <p className="md:text-sm text-xs text-blue-200">
                Există proceduri scrise și aprobate?
              </p>
            </div>
            <div className="bg-white/10 rounded-xl md:p-4 p-3">
              <h3 className="font-heading font-semibold md:text-sm text-xs mb-2">
                Maturitatea Implementării (1-5)
              </h3>
              <p className="md:text-sm text-xs text-blue-200">
                Procedura este aplicată corect în practică?
              </p>
            </div>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="md:text-lg text-base">
              Scara de Evaluare 1-5
            </CardTitle>
            <CardDescription className="md:text-sm text-xs">
              Criteriile pentru fiecare nivel de maturitate
            </CardDescription>
          </CardHeader>
          <CardContent className="md:text-sm text-xs p-0 md:p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="md:p-3 p-2 text-xs md:text-sm text-left font-medium text-slate-600">
                      Scor
                    </th>
                    <th className="md:p-3 p-2 text-xs md:text-sm text-left font-medium text-slate-600">
                      Maturitatea Documentației
                    </th>
                    <th className="md:p-3 p-2 text-xs md:text-sm text-left font-medium text-slate-600">
                      Maturitatea Implementării
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {MATURITY_SCALE.map((row) => (
                    <tr key={row.scor} className="hover:bg-slate-50">
                      <td className="md:p-3 p-2">
                        <span
                          className={`inline-flex items-center justify-center md:w-8 md:h-8 w-6 h-6 rounded-full font-bold ${
                            row.scor >= 4
                              ? "bg-emerald-100 text-emerald-700"
                              : row.scor >= 3
                                ? "bg-blue-100 text-blue-700"
                                : row.scor >= 2
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700"
                          }`}
                        >
                          {row.scor}
                        </span>
                      </td>
                      <td className="md:p-3 p-2 text-xs md:text-sm text-slate-700">
                        {row.doc}
                      </td>
                      <td className="md:p-3 p-2 text-xs md:text-sm text-slate-700">
                        {row.impl}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <div className="bg-red-50 rounded-2xl border border-red-200 p-5 mb-6">
          <h3 className="font-heading font-semibold md:text-sm text-xs text-red-800 flex items-center gap-2 mb-3">
            <AlertTriangle size={20} />
            Praguri Critice pentru Entități Esențiale
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:p-3 p-2 bg-white rounded-lg">
              <p className="md:text-sm text-xs text-red-700">
                Scor mediu minim:{" "}
                <strong className="md:text-lg text-sm">3.5</strong>
              </p>
            </div>
            <div className="md:p-3 p-2 bg-white rounded-lg">
              <p className="md:text-sm text-xs text-red-700">
                Scor minim pe fiecare măsură-cheie:{" "}
                <strong className="md:text-lg text-sm">3</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl md:p-6 p-4 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-heading font-bold md:text-xl text-lg">Output</h3>
              <p className="text-blue-100 md:text-sm text-xs mt-1">
                NIS2PASS generează direct fișierul Excel (EVAL_MMS) gata de
                transmis către DNSC.
              </p>
            </div>
            <button
              disabled
              className="md:px-6 px-4 md:py-3 py-2 md:text-sm text-xs bg-white text-blue-600 font-medium rounded-xl flex items-center gap-2 opacity-50 cursor-not-allowed"
            >
              <Play size={18} />
              În curând
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GhidMaturitate;
